import { useQueries } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useApiClient } from "@/api/client-context";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TITLE_METADATA_KEY } from "@/domain/case/case-config";
import { isFormSchemaFile } from "@/domain/process-model/form-files";
import {
  declaredFormFields,
  mergeMetadataPaths,
  type MetadataPath,
} from "@/domain/process-model/metadata-paths";

import { summarizeFormSchema } from "./summarize-form-schema";
import { useSaveMetadataPaths } from "./use-metadata-paths";
import { processModelFileQueryOptions } from "./use-process-model";

export type MetadataPathsSectionProps = {
  readonly processModelId: string;
  /** モデル内のフォーム定義ファイル名。 */
  readonly formFiles: readonly string[];
  readonly current: readonly MetadataPath[];
};

/**
 * 案件一覧・詳細に出す項目（`metadata_extraction_paths`）の編集。
 *
 * 宣言していない項目は、入力されていても案件詳細に出ない（backend が metadata として
 * 抽出しない）。フォームに項目を足しただけでは見えず、設計者からは原因が分からない
 * ——という二重管理を解消するための画面。
 *
 * 選択肢はモデルのフォーム定義から集める。フォームに無い宣言（スクリプトが書く判定
 * など）はここでは扱えないので、保存時もそのまま残す。
 */
export function MetadataPathsSection({
  processModelId,
  formFiles,
  current,
}: MetadataPathsSectionProps) {
  const { t } = useTranslation("common");
  const client = useApiClient();
  const save = useSaveMetadataPaths();

  // 項目を集めるため、フォーム定義（*-schema.json）だけを読む
  const schemaFiles = formFiles.filter(isFormSchemaFile);
  const schemas = useQueries({
    queries: schemaFiles.map((fileName) =>
      processModelFileQueryOptions(client, processModelId, fileName),
    ),
  });

  const isPending = schemas.some((query) => query.isPending);
  const fields = collectFields(schemas.map((query) => query.data?.contents));
  const fieldNames = fields.map((field) => field.name);

  const [selected, setSelected] = useState<readonly string[] | null>(null);
  const checked = selected ?? declaredFormFields(current, fieldNames);

  if (schemaFiles.length === 0) return null;

  return (
    <section
      aria-label={t("processDesign.metadata.title")}
      className="flex flex-col gap-2"
    >
      <h3 className="text-sm font-semibold">
        {t("processDesign.metadata.title")}
      </h3>
      <p className="text-muted-foreground text-xs">
        {t("processDesign.metadata.description")}
      </p>

      {isPending ? <Skeleton className="h-16 w-full" /> : null}

      <ul className="flex flex-col gap-1">
        {fields.map((field) => (
          <li key={field.name}>
            <FieldToggle
              name={field.name}
              label={field.label}
              checked={checked.includes(field.name)}
              // 件名は一覧と検索の土台。外せると画面が壊れる
              disabled={field.name === TITLE_METADATA_KEY}
              onChange={(next) =>
                setSelected(
                  next
                    ? [...checked, field.name]
                    : checked.filter((name) => name !== field.name),
                )
              }
            />
          </li>
        ))}
      </ul>

      <div className="flex items-center gap-3">
        <Button
          type="button"
          size="sm"
          disabled={save.isPending}
          onClick={() =>
            save.mutate({
              processModelId,
              paths: mergeMetadataPaths(current, checked, fieldNames),
            })
          }
        >
          {t("processDesign.metadata.save")}
        </Button>
        {save.isSuccess ? (
          <span className="text-sm font-medium">
            {t("processDesign.saved")}
          </span>
        ) : null}
        {save.isError ? (
          <span role="alert" className="text-destructive text-sm">
            {t("processDesign.metadata.saveFailed")}
          </span>
        ) : null}
      </div>
    </section>
  );
}

function FieldToggle({
  name,
  label,
  checked,
  disabled,
  onChange,
}: {
  readonly name: string;
  readonly label: string;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onChange: (_checked: boolean) => void;
}) {
  const id = useId();

  return (
    <div className="flex items-center gap-2 text-sm">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label htmlFor={id} className="flex items-baseline gap-2">
        <span>{label}</span>
        <span className="text-muted-foreground font-mono text-xs">{name}</span>
      </label>
    </div>
  );
}

/** フォーム定義から入力項目を集める（同じ名前は 1 つにまとめる）。 */
function collectFields(
  contents: readonly (string | undefined)[],
): readonly { readonly name: string; readonly label: string }[] {
  const found = new Map<string, string>();

  for (const text of contents) {
    if (text === undefined) continue;

    for (const field of summarizeFormSchema(parseJson(text))) {
      if (!found.has(field.name)) found.set(field.name, field.label);
    }
  }

  return [...found].map(([name, label]) => ({ name, label }));
}

function parseJson(contents: string): unknown {
  try {
    return JSON.parse(contents);
  } catch {
    return null;
  }
}
